import React, { useEffect, useRef, useState } from "react";
import BaseDatePicker from "./BaseDatePicker";
import { twMerge } from "tailwind-merge";
import { getBasicInputClassNames } from "./getBasicInputClassNames";

type Props = Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "className"
> & {
    label?: string;
    classes?: { root?: string; button?: string };
    date: Date | null;
    onChangeDate?: (date: Date | null) => void;
    disabled?: boolean;
    isDirty?: boolean;
    hasError?: boolean;
};

function DateInput({
    label,
    classes,
    date,
    onChangeDate,
    disabled = false,
    isDirty = false,
    hasError = false,
    ...inputProps
}: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const buttonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (!(event.target instanceof HTMLElement)) return;
            if (
                !event.target.closest(".react-datepicker") &&
                buttonRef.current !== event.target &&
                !buttonRef.current?.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isOpen]);

    const handleChange = (date: any) => {
        setIsOpen(!isOpen);
        onChangeDate?.(date);
    };
    const handleClick = (e: any) => {
        e.preventDefault();
        setIsOpen(!isOpen);
    };

    return (
        <div className={`${classes?.root} relative flex items-center`}>
            <button
                className={twMerge(
                    getBasicInputClassNames({
                        disabled,
                        error: hasError,
                        isDirty,
                    }),
                    classes?.button
                )}
                onClick={handleClick}
                ref={buttonRef}
                type="button"
                {...inputProps}
            >
                <span className="">
                    <span className="max-xl:hidden">
                        {date?.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </span>
                    <span className="xl:hidden">
                        {date?.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                        })}
                    </span>
                </span>
            </button>
            {isOpen && (
                <BaseDatePicker startDate={date} onChange={handleChange} />
            )}
        </div>
    );
}

export default DateInput;
